// Advice guides. Genuinely useful, plain-English, and deliberately free of any
// figures or prices (per the strict no-invented-facts rule). Each guide has its
// own H2 sub-sections and an FAQ block.

export const GUIDES = [
  {
    id: "house-extension-costs",
    path: "/advice/house-extension-costs",
    name: "What affects the cost of a house extension",
    cardBlurb: "The real drivers behind an extension budget — and where the money tends to go.",
    title: "What Affects House Extension Costs | DMS Home Improvements",
    metaDescription:
      "A plain-English guide to what drives the cost of a house extension — size, groundworks, glazing, finishes and more — from builders in Molesey and Kingston. No sales pitch.",
    h1: "What Affects the Cost of a House Extension",
    intro:
      "Everyone wants a number, but the honest answer to “how much does an extension cost?” is “it depends” — and this guide explains exactly what it depends on. Understanding the drivers below will help you plan a realistic budget and see why a proper site visit beats a figure plucked from the air.",
    sections: [
      {
        heading: "Size and shape",
        paragraphs: [
          "The obvious driver is how much extra floor area you are adding, but shape matters just as much as size. A simple square or rectangular extension is far more economical to build than one with lots of corners, angles or a complicated roof, because every extra corner adds foundations, walls, roof detailing and labour.",
          "A single-storey extension and a two-storey extension of the same footprint are not simply double the price of each other either. Going up a second storey reuses the same foundations and roof, so the additional space upstairs is often better value per square metre than the ground floor below it.",
        ],
      },
      {
        heading: "Groundworks and what's underground",
        paragraphs: [
          "What lies beneath your garden has a real effect on cost, and it is the part nobody can see until work starts. Ground conditions, the depth of foundations required, and the location of drains, trees and sewers can all push the groundworks up or down. Building near a public sewer, for example, can mean extra work and approvals.",
          "This is why a good builder will look carefully at the site and be upfront about the unknowns rather than pretending everything below ground is guaranteed. Sensible planning here prevents nasty surprises later.",
        ],
      },
      {
        heading: "Glazing, roof and how open you want it",
        paragraphs: [
          "Big expanses of glass — bi-fold or sliding doors, roof lanterns and large windows — are one of the biggest swing factors in an extension budget. They transform how a space feels, but glazing and the structural steel needed to hold up large openings cost considerably more than a solid wall.",
          "The roof style matters too. A flat roof is generally simpler than a pitched roof, and a lantern or glazed roof adds cost again. Deciding early how light and open you want the space to be helps set a realistic budget from the start.",
        ],
      },
      {
        heading: "Finishes and fit-out",
        paragraphs: [
          "Two extensions of identical size can differ enormously once you get to the fit-out. Kitchens, bathrooms, flooring, underfloor heating, built-in joinery and the quality of tiles and fittings are where personal choice really shows up in the final figure. A mid-range kitchen and a high-end one can be worlds apart.",
          "It is worth separating the “shell” cost of building the extension from the “fit-out” cost of what goes inside it, because the fit-out is where you have the most control over spend.",
        ],
      },
      {
        heading: "Why a fixed written quote matters",
        paragraphs: [
          "Because so much depends on your specific house and choices, the only reliable way to know what your extension will cost is to have a builder visit, measure up, understand what you want and put it in writing. A fixed written quote turns all those variables into a clear price you can plan around.",
          "That is how we work: a free site visit, a proper conversation about what you are trying to achieve, and an itemised written quote so you can see where the money is going before you commit to anything.",
        ],
      },
    ],
    faqs: [
      {
        q: "Why won't a builder give me a price over the phone?",
        a: "Because too much depends on your particular house — the ground, the access, the design and the finishes. A phone figure would either be padded to be safe or too low to be real. A short site visit lets us give you an accurate, fixed written quote instead of a guess.",
      },
      {
        q: "Is a two-storey extension twice the price of a single-storey one?",
        a: "Usually not. The upper floor shares the same foundations and roof as the ground floor, so the extra space upstairs often works out better value per square metre. On a site visit we can compare the options for your home.",
      },
    ],
  },
  {
    id: "loft-conversion-timeline",
    path: "/advice/loft-conversion-timeline",
    name: "How long a loft conversion takes",
    cardBlurb: "The stages of a loft conversion and what determines how long it takes.",
    title: "How Long Does a Loft Conversion Take? | DMS Home Improvements",
    metaDescription:
      "A stage-by-stage guide to how long a loft conversion takes and what affects the timeline, from builders in Molesey and Kingston. What to expect from start to finish.",
    h1: "How Long Does a Loft Conversion Take?",
    intro:
      "A loft conversion is one of the least disruptive ways to add a proper room to your home, because much of the early work happens up in the roof rather than through your living space. This guide walks through the stages and explains what makes one conversion quicker than another.",
    sections: [
      {
        heading: "Before anyone lifts a tool",
        paragraphs: [
          "The part people forget is the lead-in: design, structural calculations, and any approvals needed before work can start. Getting the drawings and structural details right, and confirming whether your conversion is permitted development or needs a planning application, takes time but saves a great deal of trouble later.",
          "A well-organised builder will run as much of this in parallel as possible so you are not waiting on one thing at a time. It is worth being patient at this stage — decisions made here shape the whole job.",
        ],
      },
      {
        heading: "The structural stage",
        paragraphs: [
          "Once work begins, the first big phase is structural: strengthening the floor so the loft can safely be used as a room, installing steel beams where needed, and forming the new openings for a dormer or rooflights. This is the noisiest, most intensive part of the job.",
          "If you are having a dormer built, this is when the roof is opened up and the new structure goes on. Weather can play a part here, which is one reason timelines flex a little in practice.",
        ],
      },
      {
        heading: "Making it weathertight and fitting out",
        paragraphs: [
          "After the structure is in and the roof is made weathertight, attention turns inside: insulation, the new staircase, first-fix plumbing and electrics, plasterboarding and then plastering. This is where the space starts to feel like an actual room rather than a building site.",
          "The staircase is a key moment, because it connects the new floor to the rest of the house and often affects the layout of the floor below. Good planning means it lands in the right place without stealing space from the room beneath.",
        ],
      },
      {
        heading: "Second fix and finishing",
        paragraphs: [
          "The final phase is second fix and finishing: fitting the bathroom or en-suite if you are having one, hanging doors, fitting skirting and architrave, connecting the electrics and lighting, decorating and snagging. These jobs are less disruptive but they are what make the room feel finished and cared for.",
          "A tidy, well-run finishing stage is often what separates a good conversion from an average one, so it is worth not rushing it.",
        ],
      },
      {
        heading: "What makes one conversion quicker than another",
        paragraphs: [
          "The biggest factors are the type of conversion (a simple rooflight conversion is quicker than a large dormer or a hip-to-gable), whether a bathroom is included, the complexity of the staircase, and how quickly decisions and materials come together. Delays usually come from indecision or late choices rather than the building work itself.",
          "The best thing you can do to keep a loft conversion on track is to make your key decisions early. On our free site visit we will set out a realistic sequence for your specific loft so you know what to expect week by week.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I live in the house during a loft conversion?",
        a: "In most cases yes. Much of the early work happens in the roof and is accessed from outside or through a sealed-off area, so day-to-day life carries on with some disruption. We will explain what to expect for your particular job.",
      },
      {
        q: "What causes loft conversions to overrun?",
        a: "Most delays come from late decisions, changes mid-job, or waiting on materials and finishes — not the building work itself. Deciding on layout, bathroom and fittings early is the single best way to keep things on schedule.",
      },
    ],
  },
  {
    id: "planning-permission-extensions",
    path: "/advice/planning-permission-extensions",
    name: "Planning permission for extensions",
    cardBlurb: "Permitted development, planning applications and building regulations explained.",
    title: "Do You Need Planning Permission for an Extension? | DMS Home Improvements",
    metaDescription:
      "A plain-English guide to planning permission, permitted development and building regulations for house extensions, from builders in Molesey and Kingston.",
    h1: "Do You Need Planning Permission for an Extension?",
    intro:
      "Planning is the part of an extension that worries people most, usually because the rules sound more complicated than they are. This guide explains the difference between planning permission, permitted development and building regulations in plain English, so you know roughly where you stand before you speak to anyone.",
    sections: [
      {
        heading: "Permitted development vs planning permission",
        paragraphs: [
          "Many extensions can be built under what is called permitted development — a set of national rules that let you extend within certain limits without a full planning application. If your plans fall within those limits, you may not need planning permission at all, though it is wise to confirm your rights formally.",
          "If your plans go beyond those limits, or your permitted development rights have been removed, you will need to apply for planning permission. Neither route is “better” — it simply depends on what you want to build and where.",
        ],
      },
      {
        heading: "Where you live changes the rules",
        paragraphs: [
          "Location matters a great deal. If your home is in a conservation area, is a listed building, or has had its permitted development rights restricted, the rules are tighter and more work is likely to need formal consent. Flats and maisonettes are treated differently from houses too.",
          "Parts of our area include conservation areas and period homes, so this is something we always check early. It is far better to understand the constraints at the start than to design around them later.",
        ],
      },
      {
        heading: "A Lawful Development Certificate",
        paragraphs: [
          "Even when work is genuinely permitted development, it is often sensible to apply to the council for a Lawful Development Certificate. This is an official confirmation that your project does not need planning permission.",
          "It gives you peace of mind and, importantly, is very useful when you come to sell, because buyers and their solicitors like to see that everything was done properly and above board.",
        ],
      },
      {
        heading: "Building regulations are separate",
        paragraphs: [
          "This is the point that trips people up most: planning permission and building regulations are two different things. Planning is about whether you can build something and how it looks; building regulations are about whether it is built safely and soundly — structure, insulation, fire safety, drainage and so on.",
          "Almost all extensions need building regulations approval even when they do not need planning permission. The work is inspected at key stages, and you receive a completion certificate at the end. That certificate is another document a future buyer will expect to see.",
        ],
      },
      {
        heading: "Party Wall matters",
        paragraphs: [
          "If you are building near or on a boundary shared with a neighbour, the Party Wall etc. Act may apply. This is a separate process again, designed to protect both you and your neighbour, and it usually means giving formal notice before certain work starts.",
          "It sounds daunting but it is routine, and handling it properly keeps relations with the neighbours friendly — which is worth a lot when there is a build going on next door.",
        ],
      },
      {
        heading: "How we help",
        paragraphs: [
          "We are not a planning consultancy, but after 20+ years of building extensions we know when a project is likely to be straightforward and when it needs professional drawings and applications. On a free site visit we will give you a straight, honest steer on the route your project is likely to take and who you may need to involve.",
          "Please treat this guide as general information rather than formal planning advice — the final word always rests with your local authority and the professionals preparing your application.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does every extension need planning permission?",
        a: "No. Many extensions can be built under permitted development within set limits. Whether yours qualifies depends on the size, your property and your location. It is always worth confirming your position formally before starting.",
      },
      {
        q: "Is building regulations approval the same as planning permission?",
        a: "No, they are separate. Planning is about whether and how you can build; building regulations are about building it safely. Most extensions need building regulations approval even when they do not need planning permission.",
      },
    ],
  },
  {
    id: "plastering-costs",
    path: "/advice/plastering-costs",
    name: "What affects the cost of plastering",
    cardBlurb: "Skimming, re-plastering and rendering — what drives the price of a plastering job.",
    title: "What Affects Plastering Costs | DMS Home Improvements",
    metaDescription:
      "A plain-English guide to what affects the cost of plastering — skimming, re-plastering, dry lining and rendering — from plasterers in Molesey and Kingston. No prices, just the facts.",
    h1: "What Affects the Cost of Plastering?",
    intro:
      "Plastering prices vary more than people expect, because “plastering” covers everything from skimming one wall before decorating to re-plastering a whole house or rendering the outside. This guide explains what actually drives the cost so you can understand any quote you are given.",
    sections: [
      {
        heading: "The condition of what's already there",
        paragraphs: [
          "The single biggest factor is the state of the existing surface. Skimming a coat of fresh plaster over a wall that is sound and flat is quick and straightforward. If the old plaster is blown, cracked or crumbling, it has to be hacked off and the wall re-plastered from scratch, which is a bigger job.",
          "This is why a good plasterer will always want to see and feel the walls before quoting. Two rooms that look the same can need very different amounts of work once you tap the surface.",
        ],
      },
      {
        heading: "Skim, re-plaster or dry line?",
        paragraphs: [
          "The method makes a difference. A simple skim over an existing sound surface is the most economical. Full re-plastering — removing the old and building it back up in coats — takes longer and uses more material. Dry lining, where plasterboard is fixed to the wall and then skimmed, is a common approach for walls that are too far gone to plaster directly, or for new stud walls.",
          "Which is right depends on the wall, not on preference. Part of a plasterer's job is recommending the method that gives you a sound, lasting finish rather than the cheapest short-term fix.",
        ],
      },
      {
        heading: "Size, height and access",
        paragraphs: [
          "Quantity matters, of course — more square metres means more time and materials. But height and access matter too. A stairwell, a high ceiling or a room that needs scaffold or a tower to reach safely takes longer than a standard-height room, and that is reflected in the cost.",
          "Ceilings are generally more labour-intensive than walls, because working overhead is slower and more physical.",
        ],
      },
      {
        heading: "Rendering is a different job again",
        paragraphs: [
          "External rendering sits apart from internal plastering. It involves preparing the outside walls, often working at height, dealing with the weather, and choosing between traditional sand-and-cement render or a modern through-colour system. Each behaves and prices differently.",
          "Because render is exposed to the elements, preparation is everything. Skimping on prep is the main reason render fails early, so a proper job accounts for that time.",
        ],
      },
      {
        heading: "Preparation, protection and making good",
        paragraphs: [
          "A tidy plasterer spends time before and after the actual plastering: moving and protecting furniture, laying dust sheets, masking, and cleaning up thoroughly at the end. That care is part of a professional job and part of the price, and it is the difference between coming home to a finished room and coming home to a mess.",
          "Small extras such as removing old coving, filling chases after electrical or plumbing work, or making good around new fittings can also add to a quote — all reasonable, but worth understanding so nothing feels like a surprise.",
        ],
      },
      {
        heading: "Getting a fair, fixed price",
        paragraphs: [
          "Because plastering depends so heavily on the condition of your walls, the fairest way to price it is a quick look in person followed by a fixed written quote. That protects you from open-ended day rates and protects us from under-quoting a job that turns out to need more work.",
          "If you would like a straight, honest price for plastering anywhere in Molesey, Kingston or the surrounding area, we are happy to come and take a look.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is it cheaper to skim over old plaster or re-plaster?",
        a: "Skimming over a sound surface is usually the most economical option. Full re-plastering is only necessary when the existing plaster has failed. A plasterer needs to see the walls to tell you which your room needs.",
      },
      {
        q: "Why are ceilings often dearer than walls?",
        a: "Working overhead is slower and more physically demanding than working on a wall, and access can be trickier. That extra time is reflected in the cost of plastering a ceiling compared with a wall of the same size.",
      },
    ],
  },
  {
    id: "how-to-choose-a-builder",
    path: "/advice/how-to-choose-a-builder",
    name: "How to choose a builder",
    cardBlurb: "Practical checks that help you avoid the wrong builder and find a good one.",
    title: "How to Choose a Builder | DMS Home Improvements",
    metaDescription:
      "A practical guide to choosing a builder you can trust — what to ask, what to check and the red flags to avoid — from a family-run building firm in Molesey and Kingston.",
    h1: "How to Choose a Builder",
    intro:
      "Choosing the right builder is the most important decision you will make on any project, and it has very little to do with who is cheapest. This guide sets out the practical checks that help you tell a reliable builder from a risky one — the same standards we would want you to hold us to.",
    sections: [
      {
        heading: "Look for real, local track record",
        paragraphs: [
          "A builder who has worked in your area for years has a reputation to protect and neighbours who can vouch for them. Ask how long they have been trading and where they have worked locally. A firm that is genuinely established in your area is far less likely to cut corners or disappear than one that appears from nowhere.",
          "Being local also has practical benefits: quicker site visits, easier deliveries, and someone who can come back if you need them after the job is done.",
        ],
      },
      {
        heading: "Get everything in writing",
        paragraphs: [
          "A trustworthy builder will happily provide a clear, itemised written quote that sets out what is included and what is not. Vague verbal figures are where disputes begin. A written quote lets you compare like with like and gives you something to hold both parties to.",
          "Be wary of a quote that is dramatically lower than the others. It usually means something has been left out, and the gap tends to reappear as “extras” once you are committed.",
        ],
      },
      {
        heading: "Ask about insurance and how they work",
        paragraphs: [
          "Ask whether the builder is insured, how they structure payments, and who will actually be on site. Reasonable payment stages tied to progress are normal; a large sum demanded up front before any work is not. A good builder will explain all of this without hesitation.",
          "Ask, too, who runs the job day to day and whether the same team stays on it from start to finish. Continuity matters — you want to build a relationship with the people doing the work, not hand off to a rotating cast.",
        ],
      },
      {
        heading: "See their work and talk to past customers",
        paragraphs: [
          "Photos are useful, but ask if you can see a finished job or speak to a recent customer. Most good builders are proud of their work and happy to arrange it. Hearing from someone who has lived through a project with them tells you far more than any brochure.",
          "Pay attention to how they communicated, whether they kept the site tidy, and whether they turned up when they said they would — the everyday things that make a build bearable.",
        ],
      },
      {
        heading: "Trust how they make you feel",
        paragraphs: [
          "Finally, trust your instinct about how a builder treats you at the quoting stage. Do they listen to what you want? Do they explain things clearly and honestly, including the things you might not want to hear? Are they straight about what they can and cannot do? How someone behaves before you have paid them a penny is the best guide to how they will behave once work is underway.",
          "The right builder should leave you feeling informed and reassured, not pressured or confused.",
        ],
      },
      {
        heading: "How we measure up",
        paragraphs: [
          "We are a family-run firm that has worked across Molesey, Kingston and the surrounding area for over 20 years. We give free site visits and fixed written quotes, the same core team sees your job through, and we are happy to talk through insurance, payment stages and our recent work when you get in touch.",
          "If that is the kind of builder you are looking for, we would be glad to come and take a look at your project.",
        ],
      },
    ],
    faqs: [
      {
        q: "Should I just go with the cheapest quote?",
        a: "Rarely. A quote that is far below the others usually means work has been left out, which reappears as extras later. Compare detailed written quotes on a like-for-like basis and weigh them alongside track record and how the builder communicates.",
      },
      {
        q: "What should a fair payment schedule look like?",
        a: "Reasonable stage payments tied to progress on the job are normal. Be cautious of any builder asking for a large sum up front before meaningful work has started. A good builder will explain their payment stages clearly in writing.",
      },
    ],
  },
];

export const getGuideBySlug = (path) => GUIDES.find((g) => g.path === path);
